import React, { useState } from 'react';
import { Send, CheckCircle2, Mail, Phone, MapPin } from 'lucide-react';
import { studioData } from '../data/portfolioData';

export const InquiryForm: React.FC = () => {
  const [selectedTypology, setSelectedTypology] = useState('Residential');
  const [selectedScale, setSelectedScale] = useState('300 - 600 m²');
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    location: '',
    message: ''
  });

  const typologies = ['Residential', 'Commercial', 'Cultural', 'Interior Architecture', 'Adaptive Reuse'];
  const scales = ['Under 300 m²', '300 - 600 m²', '600 - 1,500 m²', '1,500+ m²', 'Masterplanning'];

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    // Prepare Netlify Form Submission
    const form = e.currentTarget;
    const data = new FormData(form);

    fetch('/', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams(data as any).toString(),
    })
      .then(() => {
        setSubmitted(true);
      })
      .catch((error) => {
        console.error('Submission error:', error);
        // Fallback to submitted state for template demonstration
        setSubmitted(true);
      });
  };

  return (
    <section id="inquire" className="py-24 px-6 md:px-12 max-w-7xl mx-auto relative z-10 border-t border-white/10 scroll-mt-24">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-start">
        {/* Left Information Column */}
        <div className="lg:col-span-5 space-y-8">
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-xs font-mono text-arch-accent uppercase tracking-widest">
              <span className="w-1.5 h-1.5 rounded-full bg-arch-accent" />
              <span>06 // Project Inquiries</span>
            </div>
            <h2 className="text-3xl sm:text-5xl font-serif text-white tracking-tight leading-tight">
              Initiate a <span className="italic font-normal text-arch-limestone">Dialogue</span>
            </h2>
            <p className="text-xs sm:text-sm text-neutral-300 font-light leading-relaxed">
              We welcome commissions for thoughtful private residences, commercial landmarks, and selective cultural spaces. Share your site location, timeline, and vision below.
            </p>
          </div>

          <div className="space-y-4 pt-4 border-t border-white/10 text-xs font-mono">
            <div className="flex items-center gap-3 text-neutral-300">
              <Mail className="w-4 h-4 text-arch-accent" />
              <a href={`mailto:${studioData.email}`} className="hover:text-white transition-colors">
                {studioData.email}
              </a>
            </div>

            <div className="flex items-center gap-3 text-neutral-300">
              <Phone className="w-4 h-4 text-arch-accent" />
              <span>{studioData.phone}</span>
            </div>

            <div className="flex items-center gap-3 text-neutral-300">
              <MapPin className="w-4 h-4 text-arch-accent" />
              <span>{studioData.location}</span>
            </div>
          </div>

          <div className="p-5 rounded-sm bg-white/[0.02] border border-white/5 space-y-2 text-xs font-mono">
            <div className="text-arch-accent uppercase tracking-wider text-[11px] font-semibold">
              Design Confidentiality Guarantee
            </div>
            <p className="text-neutral-400 font-light text-[11px]">
              All project briefs, site parameters, and residential consultations are treated with absolute discretion under standard architectural non-disclosure protocols.
            </p>
          </div>
        </div>

        {/* Right Form Column */}
        <div className="lg:col-span-7">
          {submitted ? (
            <div className="p-10 rounded-sm bg-arch-surface/80 border border-arch-accent/40 text-center space-y-5">
              <div className="w-12 h-12 rounded-full bg-arch-accent/20 border border-arch-accent text-arch-accent mx-auto flex items-center justify-center">
                <CheckCircle2 className="w-6 h-6" />
              </div>
              <h3 className="text-2xl font-serif text-white">Project Inquiry Received</h3>
              <p className="text-sm text-neutral-300 max-w-md mx-auto font-light leading-relaxed">
                Thank you for considering our studio. Arch. Dominic &ldquo;Hutch&rdquo; Glarino and the design team will review your site parameters and respond within 48 business hours.
              </p>
              <button
                onClick={() => setSubmitted(false)}
                className="mt-4 px-6 py-2 rounded-sm bg-white/5 border border-white/10 text-xs font-mono text-arch-accent hover:bg-white/10"
              >
                Submit Another Request
              </button>
            </div>
          ) : (
            <form
              name="architect-inquiry"
              method="POST"
              data-netlify="true"
              data-netlify-honeypot="bot-field"
              onSubmit={handleSubmit}
              className="p-8 sm:p-10 rounded-sm bg-arch-card/80 border border-white/10 space-y-8"
            >
              {/* Hidden Netlify field */}
              <input type="hidden" name="form-name" value="architect-inquiry" />
              <input type="hidden" name="bot-field" />
              <input type="hidden" name="projectType" value={selectedTypology} />
              <input type="hidden" name="estimatedBudget" value={selectedScale} />

              {/* Typology Select Buttons */}
              <div className="space-y-3">
                <label className="block text-xs font-mono text-arch-muted uppercase tracking-wider">
                  Select Project Typology
                </label>
                <div className="flex flex-wrap gap-2">
                  {typologies.map((t) => (
                    <button
                      type="button"
                      key={t}
                      onClick={() => setSelectedTypology(t)}
                      className={`px-3.5 py-1.5 rounded-sm text-xs font-mono uppercase tracking-wider transition-all ${
                        selectedTypology === t
                          ? 'bg-arch-accent text-[#0A0B0D] font-semibold'
                          : 'bg-white/5 border border-white/10 text-neutral-300 hover:border-white/30'
                      }`}
                    >
                      {t}
                    </button>
                  ))}
                </div>
              </div>

              {/* Scale / Area Buttons */}
              <div className="space-y-3">
                <label className="block text-xs font-mono text-arch-muted uppercase tracking-wider">
                  Estimated Floor Area / Scope
                </label>
                <div className="flex flex-wrap gap-2">
                  {scales.map((s) => (
                    <button
                      type="button"
                      key={s}
                      onClick={() => setSelectedScale(s)}
                      className={`px-3 py-1.5 rounded-sm text-xs font-mono tracking-wider transition-all ${
                        selectedScale === s
                          ? 'bg-arch-accent text-[#0A0B0D] font-semibold'
                          : 'bg-white/5 border border-white/10 text-neutral-300 hover:border-white/30'
                      }`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>

              {/* Contact Inputs */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="block text-xs font-mono text-arch-muted uppercase tracking-wider">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g. Elena Rostova"
                    className="w-full bg-white/[0.03] border border-white/10 focus:border-arch-accent rounded-sm px-4 py-3 text-sm text-white focus:outline-none transition-colors"
                  />
                </div>

                <div className="space-y-2">
                  <label className="block text-xs font-mono text-arch-muted uppercase tracking-wider">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="e.g. client@example.com"
                    className="w-full bg-white/[0.03] border border-white/10 focus:border-arch-accent rounded-sm px-4 py-3 text-sm text-white focus:outline-none transition-colors"
                  />
                </div>

                <div className="space-y-2">
                  <label className="block text-xs font-mono text-arch-muted uppercase tracking-wider">
                    Contact Phone Number
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="+63 917 000 0000"
                    className="w-full bg-white/[0.03] border border-white/10 focus:border-arch-accent rounded-sm px-4 py-3 text-sm text-white focus:outline-none transition-colors"
                  />
                </div>

                <div className="space-y-2">
                  <label className="block text-xs font-mono text-arch-muted uppercase tracking-wider">
                    Site Location (City / Province)
                  </label>
                  <input
                    type="text"
                    name="location"
                    value={formData.location}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                    placeholder="e.g. Tagaytay / Alabang"
                    className="w-full bg-white/[0.03] border border-white/10 focus:border-arch-accent rounded-sm px-4 py-3 text-sm text-white focus:outline-none transition-colors"
                  />
                </div>
              </div>

              {/* Message Box */}
              <div className="space-y-2">
                <label className="block text-xs font-mono text-arch-muted uppercase tracking-wider">
                  Project Vision, Brief or Timeline
                </label>
                <textarea
                  name="message"
                  rows={4}
                  required
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  placeholder="Describe your site topography, desired program, or spatial aspirations..."
                  className="w-full bg-white/[0.03] border border-white/10 focus:border-arch-accent rounded-sm p-4 text-sm text-white focus:outline-none transition-colors resize-none"
                />
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                className="w-full flex items-center justify-center gap-3 py-4 rounded-sm bg-arch-accent text-[#0A0B0D] font-mono text-xs uppercase tracking-widest font-semibold hover:bg-white transition-colors duration-300 shadow-xl"
              >
                <span>Transmit Project Inquiry</span>
                <Send className="w-4 h-4" />
              </button>

              <div className="text-center text-[10px] font-mono text-neutral-500">
                Direct transmission to Dominic &ldquo;Hutch&rdquo; Glarino Architecture • Confidential consultation guarantee
              </div>
            </form>
          )}
        </div>
      </div>
    </section>
  );
};
